import React from 'react'
import KibDatepicker, {
  type KibDatepickerProps,
} from '@chewy/kib-datepicker-react'
import moment from 'moment-timezone'
import { useDebouncedCallback } from 'use-debounce'

export interface RhapKibDatePickerProps
  extends Omit<
    KibDatepickerProps,
    'value' | 'onChange' | 'minDate' | 'maxDate'
  > {
  /**
   * Date input format string (default: "MM/dd/yyyy").
   * Used for validation of keyboard input.
   */
  inputFormat?: string
  /**
   * Maximum date as ISO string in business timezone (optional)
   */
  maxDate?: string | null
  /**
   * Minimum date as ISO string in business timezone (optional)
   */
  minDate?: string | null
  /**
   * Callback when the date changes, returns ISO string in business timezone
   */
  onChange: (isoDate: string | null) => void
  /**
   * Business timezone for date conversion
   */
  timezone: string
  /**
   * The date value as an ISO string in business timezone
   */
  value: string | null | undefined
}

/**
 * Helper function to convert business timezone ISO date to local timezone Date object
 * for KibDatepicker display. This ensures KibDatepicker displays the correct date
 * regardless of user's local timezone.
 */
export const createDateForKibDatepicker = (
  isoDateString: string | null | undefined,
  timezone: string,
): Date | null => {
  if (!isoDateString) {
    return null
  }

  const businessMoment = moment.tz(isoDateString, timezone)
  // This tricks KibDatepicker (which uses date-fns) into showing the correct date
  // by creating a local Date with the same year/month/day as the business date
  return new Date(
    businessMoment.year(),
    businessMoment.month(),
    businessMoment.date(),
  )
}

/**
 * Helper function to convert local Date object back to business timezone ISO string
 */
export const convertLocalDateToBusinessTimezone = (
  localDate: Date,
  timezone: string,
): string => {
  const businessDate = moment.tz(
    {
      year: localDate.getFullYear(),
      month: localDate.getMonth(),
      date: localDate.getDate(),
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    },
    timezone,
  )

  return businessDate.toISOString()
}

/**
 * Checks if raw keyboard input should be rejected (invalid format).
 * Used to skip processing incomplete or incorrectly formatted dates (e.g., 2-digit years).
 */
export const isInputInvalid = (
  rawInput: string | undefined,
  inputFormat: string,
): boolean => {
  const pattern = inputFormat.toUpperCase()
  return Boolean(rawInput && !moment(rawInput, pattern, true).isValid())
}

/**
 * Timezone-aware wrapper for KibDatepicker that handles conversion between
 * business timezone ISO strings and local timezone Date objects for display.
 */
const RhapKibDatePicker: React.FC<RhapKibDatePickerProps> = ({
  value,
  onChange,
  timezone,
  minDate,
  maxDate,
  inputFormat = 'MM/dd/yyyy',
  ...kibDatepickerProps
}) => {
  const handleChange = useDebouncedCallback(
    (newValue: Date | null, rawValue?: string) => {
      if (!newValue || Number.isNaN(new Date(newValue).getTime())) {
        onChange(null)
        return
      }

      // Skip processing if user is still typing or entered incomplete/invalid format
      if (isInputInvalid(rawValue, inputFormat)) {
        return
      }

      const businessIsoDate = convertLocalDateToBusinessTimezone(
        newValue,
        timezone,
      )

      onChange(businessIsoDate)
    },
    1000,
  )

  return (
    <KibDatepicker
      inputFormat={inputFormat}
      {...kibDatepickerProps}
      maxDate={createDateForKibDatepicker(maxDate, timezone)}
      minDate={createDateForKibDatepicker(minDate, timezone)}
      value={createDateForKibDatepicker(value, timezone)}
      onChange={handleChange}
    />
  )
}

export default RhapKibDatePicker
